/**
 * Admin Email Log — visibility into everything the outbound safety gate
 * did (and didn't) send. Landed 28 Feb 2026 after the Feb 27 ticket #429
 * incident where blocked emails were invisible until someone spotted a
 * missing notification.
 *
 * Every send attempt (allowed OR blocked) is here. Blocked entries carry
 * the reason and the full payload, so an admin can hit "Resend" and
 * replay it. The resend bypasses the rate/settings caps but STILL
 * respects the env kill-switch (preview cannot send under any
 * circumstance).
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { ArrowClockwise, ArrowsClockwise, Envelope, EnvelopeSimple, Funnel, Info, WarningCircle, CheckCircle, XCircle } from "@phosphor-icons/react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/lib/auth";
import api from "@/lib/api";

// Human-readable copy per status. Kept as data so future statuses just
// need one entry here.
const STATUS_META = {
  sent:               { label: "Sent",              color: "text-emerald-800 bg-emerald-50 border-emerald-200", icon: CheckCircle, blurb: "Delivered to Microsoft Graph." },
  blocked_env:        { label: "Blocked · env",     color: "text-slate-800 bg-slate-100 border-slate-300",      icon: XCircle,     blurb: "Environment kill-switch is on. Preview never sends real mail." },
  blocked_settings:   { label: "Blocked · settings",color: "text-slate-800 bg-slate-100 border-slate-300",      icon: XCircle,     blurb: "Admin toggle: email_send_enabled=false." },
  blocked_hourly:     { label: "Blocked · hourly",  color: "text-amber-900 bg-amber-50 border-amber-200",       icon: WarningCircle, blurb: "Global 60-min send cap reached." },
  blocked_recipient:  { label: "Blocked · recipient", color: "text-amber-900 bg-amber-50 border-amber-200",     icon: WarningCircle, blurb: "That recipient hit the per-hour cap." },
  blocked_duplicate:  { label: "Blocked · duplicate", color: "text-red-900 bg-red-50 border-red-200",           icon: WarningCircle, blurb: "Same subject to same recipient repeatedly — treated as a runaway loop." },
};

function fmtTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return iso; }
}

export default function EmailLog() {
  const { user } = useAuth();
  const [status, setStatus] = useState("all");
  const [toFilter, setToFilter] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [previewEntry, setPreviewEntry] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [resendingId, setResendingId] = useState(null);

  // Admins/managers can view; only admins can resend (enforced backend too).
  const canResend = user?.role === "admin";

  const load = () => {
    setLoading(true);
    const params = { limit: 200 };
    if (status && status !== "all") params.status = status;
    if (toFilter.trim()) params.to = toFilter.trim();
    api.get("/admin/email/send-log", { params })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.response?.data?.detail || "Failed to load email log"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, toFilter]);

  const openPreview = async (entry) => {
    // Legacy pre-Feb-28 entries have no id or no stored payload —
    // show what we have from the list row rather than firing a 404
    // that flashes the drawer closed.
    if (!entry.id) {
      setPreviewEntry({ ...entry, has_payload: false, _legacy: true });
      return;
    }
    setPreviewEntry({ ...entry, _loading: true });
    setPreviewLoading(true);
    try {
      const r = await api.get(`/admin/email/send-log/${entry.id}`);
      setPreviewEntry(r.data);
    } catch (e) {
      // Fall back to the row-level data we already have so the drawer
      // stays open — matches the "not found" copy exactly so admins
      // aren't left with an empty modal.
      setPreviewEntry({ ...entry, has_payload: false, _fetchError: e?.response?.data?.detail || "Failed to load full entry" });
    } finally {
      setPreviewLoading(false);
    }
  };

  const resend = async (entry) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Resend this email to ${entry.to}?\n\nSubject: ${entry.subject}\n\nThis bypasses the rate caps but not the environment kill-switch.`)) return;
    setResendingId(entry.id);
    try {
      const r = await api.post(`/admin/email/send-log/${entry.id}/resend`);
      // Endpoint returns 202-style — the actual Graph send happens in
      // a background task. Refresh the log after a short delay so the
      // user sees the new "Sent" row (or the "Retried" chip) appear.
      toast.success(r.data?.detail || "Resend queued. Refreshing the log…", { duration: 4000 });
      setResendingId(null);
      // Poll twice so a slow Graph response still shows up without the
      // user having to click Refresh themselves.
      setTimeout(load, 3000);
      setTimeout(load, 8000);
      if (previewEntry?.id === entry.id) {
        setTimeout(() => openPreview(entry), 4000);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend failed");
      setResendingId(null);
    }
  };

  // Roll-up counts — surface the "blocked in the last hour" number
  // prominently so a spike is impossible to miss.
  const blocked1h = useMemo(() => {
    if (!data?.counts_last_hour) return 0;
    return Object.entries(data.counts_last_hour)
      .filter(([k]) => k.startsWith("blocked_"))
      .reduce((s, [, n]) => s + n, 0);
  }, [data]);
  const sent1h = data?.counts_last_hour?.sent || 0;
  const blocked24h = useMemo(() => {
    if (!data?.counts_last_24h) return 0;
    return Object.entries(data.counts_last_24h)
      .filter(([k]) => k.startsWith("blocked_"))
      .reduce((s, [, n]) => s + n, 0);
  }, [data]);

  const entries = data?.entries || [];

  return (
    <>
    <div className="max-w-6xl mx-auto space-y-6 p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-1">Admin · Deliverability</div>
            <h1 className="font-heading text-3xl font-bold tracking-tight">Email log</h1>
            <p className="text-sm text-slate-500 mt-1 max-w-2xl">
              Every outbound email attempt from the last 30 days, whether it was delivered or blocked by the safety gate. Blocked entries can be manually resent by an admin.
            </p>
          </div>
          <Button
            variant="outline"
            onClick={load}
            disabled={loading}
            data-testid="email-log-refresh"
          >
            <ArrowsClockwise size={14} className={loading ? "animate-spin" : ""} /> Refresh
          </Button>
        </div>

        {/* Env warning banner — impossible to miss when kill-switch is on */}
        {data?.env_disabled && (
          <div className="hairline rounded-lg bg-slate-900 text-slate-100 p-4 flex items-start gap-3" data-testid="email-env-banner">
            <XCircle size={20} weight="duotone" className="text-red-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm">
              <div className="font-semibold text-white">Environment kill-switch is ON</div>
              <div className="text-slate-300 mt-1">
                <code className="text-xs">MS_GRAPH_SEND_DISABLED=1</code> is set in <code className="text-xs">.env</code>. All sends are logged but not delivered — including resends from this page. Expected in preview; unset in production.
              </div>
            </div>
          </div>
        )}

        {/* Roll-up cards */}
        <div className="grid grid-cols-4 gap-3">
          <StatCard label="Sent · last hour" value={sent1h} color="text-emerald-700" testId="stat-sent-1h" />
          <StatCard label="Blocked · last hour" value={blocked1h} color={blocked1h > 0 ? "text-amber-800" : "text-slate-500"} testId="stat-blocked-1h" />
          <StatCard label="Blocked · last 24h" value={blocked24h} color={blocked24h > 0 ? "text-amber-800" : "text-slate-500"} testId="stat-blocked-24h" />
          <StatCard label="Sent · last 24h" value={data?.counts_last_24h?.sent || 0} color="text-slate-700" testId="stat-sent-24h" />
        </div>

        {/* Caps summary — hidden after 2 Mar 2026 when the runtime caps
            were removed. Left as a mount point in case they come back. */}
        {data && data.hourly_cap != null && (
          <div className="hairline rounded-lg bg-white p-3 flex items-center gap-2 text-[11px] text-slate-500 font-mono flex-wrap">
            <Info size={13} className="text-slate-400" />
            <span>Caps in force:</span>
            <span className="text-slate-800">{data.hourly_cap}/hour global</span>
            <span>·</span>
            <span className="text-slate-800">{data.recipient_cap_per_hour}/hour per recipient</span>
            <span>·</span>
            <span className="text-slate-800">{data.duplicate_subject_cap_per_hour}/hour identical subject → same recipient</span>
          </div>
        )}

        {/* Filters */}
        <div className="hairline rounded-lg bg-white p-4 flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[220px]">
            <Label className="text-xs text-slate-500">Status</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger className="mt-1 bg-white border-slate-200" data-testid="email-log-status-filter">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-white border-slate-200">
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="sent">Sent only</SelectItem>
                <SelectItem value="blocked_env">Blocked · preview kill-switch</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="flex-1 min-w-[260px]">
            <Label className="text-xs text-slate-500">Recipient email</Label>
            <Input
              value={toFilter}
              onChange={(e) => setToFilter(e.target.value)}
              placeholder="filter by recipient (exact match)"
              className="mt-1 bg-white border-slate-200"
              data-testid="email-log-to-filter"
            />
          </div>
          {(status !== "all" || toFilter) && (
            <Button variant="ghost" size="sm" onClick={() => { setStatus("all"); setToFilter(""); }}>
              <Funnel size={13} /> Clear
            </Button>
          )}
        </div>

        {/* Entries */}
        <div className="hairline rounded-lg bg-white overflow-hidden">
          {loading && entries.length === 0 ? (
            <div className="p-12 text-center text-slate-400 text-sm">Loading…</div>
          ) : entries.length === 0 ? (
            <div className="p-12 text-center">
              <EnvelopeSimple size={28} className="mx-auto text-slate-300 mb-3" />
              <div className="text-sm text-slate-500">No entries match the current filter.</div>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider font-medium">
                <tr>
                  <th className="px-3 py-2 text-left w-36">When</th>
                  <th className="px-3 py-2 text-left w-32">Status</th>
                  <th className="px-3 py-2 text-left">Recipient</th>
                  <th className="px-3 py-2 text-left">Subject</th>
                  <th className="px-3 py-2 text-right w-28">Actions</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((e) => {
                  const meta = STATUS_META[e.status] || { label: e.status, color: "text-slate-800 bg-slate-100 border-slate-300", icon: Envelope };
                  const Icon = meta.icon;
                  const isBlocked = e.status !== "sent";
                  const alreadyRetried = !!e.retried_at;
                  return (
                    <tr
                      key={e.id}
                      className={`border-t border-slate-100 hover:bg-slate-50 cursor-pointer ${isBlocked ? "bg-amber-50/20" : ""}`}
                      onClick={() => openPreview(e)}
                      data-testid={`email-log-row-${e.id}`}
                    >
                      <td className="px-3 py-2 text-xs font-mono text-slate-500 whitespace-nowrap">{fmtTime(e.at)}</td>
                      <td className="px-3 py-2">
                        <span className={`chip !text-[11px] !py-0.5 border ${meta.color} inline-flex items-center gap-1`}>
                          <Icon size={12} weight="duotone" /> {meta.label}
                        </span>
                        {alreadyRetried && (
                          <span className="chip !text-[10px] !py-0.5 !bg-emerald-50 !text-emerald-800 !border-emerald-200 ml-1" title={`Resent by ${e.retried_by}`}>
                            Retried
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-slate-800 font-mono text-xs">{e.to}</td>
                      <td className="px-3 py-2 text-slate-800 truncate max-w-md" title={e.subject}>{e.subject}</td>
                      <td className="px-3 py-2 text-right">
                        {isBlocked && canResend && e.retryable && (
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={resendingId === e.id}
                            onClick={(ev) => { ev.stopPropagation(); resend(e); }}
                            className="h-7 text-xs"
                            data-testid={`email-log-resend-${e.id}`}
                          >
                            <ArrowClockwise size={12} className={resendingId === e.id ? "animate-spin" : ""} />
                            {resendingId === e.id ? "Sending…" : "Resend"}
                          </Button>
                        )}
                        {isBlocked && !e.retryable && (
                          <span
                            className="text-[10px] text-slate-400 font-mono italic"
                            title="Legacy log entry with no stored body and no matching reconstructable subject pattern — cannot be replayed automatically. Contact support if you need this resent."
                          >
                            Legacy
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Preview drawer — click a row to see body + full payload + resend */}
      <Dialog open={!!previewEntry} onOpenChange={(o) => !o && setPreviewEntry(null)}>
        <DialogContent className="max-w-3xl bg-white max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="pr-8">{previewEntry?.subject || "Email log entry"}</DialogTitle>
          </DialogHeader>
          {previewEntry && (
            <div className="space-y-4 text-sm">
              <PreviewMeta entry={previewEntry} />
              {previewLoading ? (
                <div className="text-slate-400 text-xs">Loading full body…</div>
              ) : previewEntry.has_payload === false ? (
                <div className="text-xs rounded-md border border-amber-200 bg-amber-50 text-amber-900 px-3 py-2">
                  {previewEntry._fetchError
                    ? previewEntry._fetchError
                    : previewEntry.reconstructable
                      ? "No stored body for this entry, but the subject matches a known pattern — clicking Resend will rebuild the body from the ticket's current state and send it. Newer entries also keep their exact original body for replay."
                      : "No stored body for this entry — it was recorded before the payload-persistence feature landed. Contact support if you need this specific email resent."}
                </div>
              ) : (
                <>
                  {previewEntry.payload?.body_text && (
                    <div>
                      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-1">Plain text body</div>
                      <pre className="whitespace-pre-wrap text-xs bg-slate-50 border border-slate-200 rounded p-3 max-h-64 overflow-auto font-mono text-slate-700">
                        {previewEntry.payload.body_text}
                      </pre>
                    </div>
                  )}
                  {previewEntry.payload?.body_html_preview && (
                    <details>
                      <summary className="text-[11px] uppercase tracking-wider text-slate-500 font-mono cursor-pointer">HTML body (rendered)</summary>
                      <div className="mt-2 border border-slate-200 rounded overflow-hidden">
                        <iframe
                          title="html preview"
                          sandbox=""
                          srcDoc={previewEntry.payload.body_html_preview}
                          className="w-full h-96 bg-white"
                        />
                      </div>
                    </details>
                  )}
                </>
              )}
              {previewEntry.status !== "sent" && canResend && previewEntry.id && (previewEntry.has_payload || previewEntry.reconstructable) && (
                <div className="flex justify-end pt-2 border-t border-slate-200">
                  <Button
                    onClick={() => resend(previewEntry)}
                    disabled={resendingId === previewEntry.id}
                    className="bg-[#4f4c90] hover:bg-[#5F5CA5]"
                    data-testid="email-log-preview-resend"
                  >
                    <ArrowClockwise size={14} className={resendingId === previewEntry.id ? "animate-spin" : ""} />
                    {resendingId === previewEntry.id
                      ? "Sending…"
                      : (previewEntry.has_payload ? "Resend this email" : "Resend (rebuild from ticket)")}
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

function StatCard({ label, value, color, testId }) {
  return (
    <div className="hairline rounded-lg bg-white p-4" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">{label}</div>
      <div className={`text-3xl font-bold mt-1 ${color}`}>{value}</div>
    </div>
  );
}

function PreviewMeta({ entry }) {
  const meta = STATUS_META[entry.status] || { label: entry.status, color: "text-slate-800 bg-slate-100 border-slate-300", blurb: "" };
  return (
    <div className="space-y-2 text-xs">
      <div className="grid grid-cols-2 gap-x-4 gap-y-1">
        <MetaRow label="When" value={fmtTime(entry.at)} />
        <MetaRow label="To" value={entry.to} mono />
        <MetaRow label="Caller" value={entry.caller} mono />
        <MetaRow label="Status" value={<span className={`chip !text-[11px] !py-0.5 border ${meta.color}`}>{meta.label}</span>} />
        {entry.reason && <MetaRow label="Reason" value={entry.reason} mono full />}
        {entry.retried_at && <MetaRow label="Retried" value={`${fmtTime(entry.retried_at)} by ${entry.retried_by}`} full />}
        {entry.resent_from && <MetaRow label="Resend of" value={entry.resent_from} mono full />}
      </div>
      {meta.blurb && (
        <div className="text-[11px] text-slate-500 italic">{meta.blurb}</div>
      )}
    </div>
  );
}

function MetaRow({ label, value, mono, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] uppercase tracking-wider text-slate-400 font-mono">{label}</div>
      <div className={`text-slate-800 ${mono ? "font-mono text-xs" : ""} break-all`}>{value}</div>
    </div>
  );
}
